import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

const roleLabel = { super_admin: 'Super Admin', admin_pesquisa: 'Admin Pesquisa', pesquisador: 'Pesquisador', visualizador: 'Visualizador' };

export default function Layout() {
  const { profile, signOut, isSuperAdmin, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarAberta, setSidebarAberta] = useState(false);

  useEffect(() => { setSidebarAberta(false); }, [location.pathname]);

  async function handleSignOut() {
    await signOut();
    navigate('/login');
  }

  const initials = profile?.nome_completo?.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() || '?';

  function NavItem({ to, icon, label, end = false }) {
    return (
      <NavLink to={to} end={end} className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}>
        <span className="icon">{icon}</span> {label}
      </NavLink>
    );
  }

  return (
    <div className="app-shell">
      <style>{`
        /* Prevenir scroll horizontal global */
        html, body, #root { overflow-x: hidden; max-width: 100vw; }
        * { box-sizing: border-box; }

        /* ── MOBILE HEADER ── */
        .mobile-header {
          display: none;
          position: fixed; top: 0; left: 0; right: 0;
          height: 54px; background: var(--navy); z-index: 300;
          align-items: center; padding: 0 16px; gap: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,.3);
        }
        .hamburger {
          background: none; border: none; cursor: pointer;
          padding: 8px; display: flex; flex-direction: column;
          gap: 5px; flex-shrink: 0;
        }
        .hamburger span {
          display: block; width: 22px; height: 2px;
          background: #fff; border-radius: 2px;
        }
        .mobile-title { color: #fff; font-size: 15px; font-weight: 600; flex: 1; min-width: 0; }
        .mobile-signout {
          background: rgba(255,255,255,.12); border: none; cursor: pointer;
          width: 36px; height: 36px; border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          color: #fff; font-size: 18px; flex-shrink: 0;
        }
        .sidebar-overlay {
          display: none; position: fixed; inset: 0;
          background: rgba(0,0,0,.55); z-index: 299;
        }

        /* ── MOBILE BREAKPOINT ── */
        @media (max-width: 768px) {
          .mobile-header { display: flex !important; }
          .sidebar {
            transform: translateX(-100%);
            transition: transform .25s ease;
            z-index: 300; width: 260px !important;
          }
          .sidebar.aberta { transform: translateX(0); }
          .sidebar-overlay { display: block; }

          .main-content {
            margin-left: 0 !important;
            padding: 66px 12px 32px !important;
            width: 100% !important;
            max-width: 100vw !important;
            overflow-x: hidden !important;
          }

          /* Stats: 2 colunas compactas */
          .stats-grid {
            grid-template-columns: 1fr 1fr !important;
            gap: 8px !important;
            margin-bottom: 16px !important;
          }
          .stat-card {
            padding: 12px 14px !important;
          }
          .stat-card .value { font-size: 24px !important; }
          .stat-card .label { font-size: 10px !important; }
          .stat-card .icon-wrap { font-size: 18px !important; margin-bottom: 4px !important; }

          /* Grids */
          .grid-2 { grid-template-columns: 1fr !important; gap: 10px !important; }

          /* Page header */
          .page-header {
            flex-direction: column !important; gap: 10px !important;
            align-items: flex-start !important; margin-bottom: 14px !important;
          }
          .page-header h1 { font-size: 19px !important; }
          .page-header p { font-size: 12px !important; }

          /* Card */
          .card { border-radius: 10px !important; }
          .card-header {
            flex-direction: column !important; gap: 8px !important;
            align-items: flex-start !important; padding: 14px 14px 10px !important;
          }
          .card-header h2 { font-size: 14px !important; }
          .card-header .form-input { width: 100% !important; max-width: 100% !important; }
          .card-body { padding: 14px !important; }

          /* Tabelas — sem scroll horizontal, colunas reduzidas */
          .table-wrap { overflow-x: hidden !important; width: 100% !important; }
          table { width: 100% !important; min-width: unset !important; }
          th, td { padding: 8px 8px !important; font-size: 12px !important; }

          /* Modais bottom sheet */
          .modal-overlay { align-items: flex-end !important; padding: 0 !important; }
          .modal {
            max-width: 100% !important; width: 100% !important;
            border-radius: 20px 20px 0 0 !important;
            max-height: 92vh !important; overflow-y: auto !important;
          }

          /* Botões */
          .btn { padding: 10px 16px !important; font-size: 14px !important; }
          .btn-sm { padding: 8px 12px !important; font-size: 13px !important; }

          /* Abas Patient View */
          .abas-nav {
            display: flex !important; gap: 4px !important;
            overflow-x: auto !important; -webkit-overflow-scrolling: touch !important;
            padding-bottom: 4px !important; flex-wrap: nowrap !important;
            scrollbar-width: none !important;
          }
          .abas-nav::-webkit-scrollbar { display: none !important; }
          .aba-btn {
            padding: 8px 12px !important; font-size: 13px !important;
            white-space: nowrap !important; flex-shrink: 0 !important;
          }

          /* Formulários — evitar zoom iOS */
          .form-input, .form-select, .form-textarea {
            font-size: 16px !important; padding: 11px 12px !important;
          }

          /* Alertas */
          .alert { font-size: 12px !important; padding: 10px 12px !important; }

          /* Cabeçalho paciente no patient view */
          .patient-header-bar {
            padding: 12px 14px !important;
            margin-bottom: 12px !important;
          }
        }
      `}</style>

      {/* Header fixo mobile com botão de logoff */}
      <div className="mobile-header">
        <button className="hamburger" onClick={() => setSidebarAberta(v => !v)} aria-label="Menu">
          <span /><span /><span />
        </button>
        <span className="mobile-title">Pesquisa Clínica</span>
        <button className="mobile-signout" onClick={handleSignOut} title="Sair" aria-label="Sair">
          ↩
        </button>
      </div>

      {sidebarAberta && <div className="sidebar-overlay" onClick={() => setSidebarAberta(false)} />}

      <aside className={`sidebar ${sidebarAberta ? 'aberta' : ''}`}>
        <div className="sidebar-logo">
          <h1>Pesquisa Clínica</h1>
          <p>Ortopedia · Coluna</p>
        </div>
        <nav className="sidebar-nav">
          <div className="nav-section">Principal</div>
          <NavItem to="/" icon="📊" label="Dashboard" end />
          <NavItem to="/pacientes" icon="🧑‍⚕️" label="Pacientes" />
          <NavItem to="/pesquisas" icon="🔬" label="Pesquisas" />
          <NavItem to="/calendario" icon="📅" label="Calendário" />
          <NavItem to="/instrucoes" icon="📋" label="Instruções" />
          <NavItem to="/exportacao" icon="⬇️" label="Exportação" />
          <NavItem to="/estatisticas" icon="📈" label="Estatísticas" />
          {isAdmin && (
            <>
              <div className="nav-section">Administração</div>
              <NavItem to="/config-pesquisa" icon="⚙️" label="Configurar pesquisa" />
            </>
          )}
          {isSuperAdmin && (
            <NavItem to="/usuarios" icon="👥" label="Usuários" />
          )}
          {isSuperAdmin && (
            <NavItem to="/audit" icon="🔍" label="Auditoria" />
          )}
        </nav>
        <div className="sidebar-footer">
          <div className="user-chip">
            <div className="avatar">{initials}</div>
            <div>
              <div className="name">{profile?.nome_completo?.split(' ')[0] || 'Usuário'}</div>
              <div className="role">{roleLabel[profile?.role] || ''}</div>
            </div>
            <button className="btn-signout" onClick={handleSignOut} title="Sair">↩</button>
          </div>
        </div>
      </aside>

      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
}
